import React from "react";

interface SourceCandidate {
  src: string;
  type?: string;
}

interface OptimizedImageProps
  extends React.ImgHTMLAttributes<HTMLImageElement> {
  src: string; // fallback src (must exist)
  sources?: SourceCandidate[]; // optional explicit modern sources
  deriveCandidates?: boolean; // if true, derive .avif/.webp from src
}

const guessType = (path: string) => {
  const ext = path.split("?")[0].split(".").pop()?.toLowerCase();
  switch (ext) {
    case "avif":
      return "image/avif";
    case "webp":
      return "image/webp";
    case "png":
      return "image/png";
    case "jpg":
    case "jpeg":
      return "image/jpeg";
    case "svg":
      return "image/svg+xml";
    default:
      return undefined;
  }
};

export function OptimizedImage({
  src,
  sources,
  deriveCandidates = true,
  alt,
  ...imgProps
}: OptimizedImageProps) {
  const [available, setAvailable] = React.useState<SourceCandidate[]>([]);

  React.useEffect(() => {
    let cancelled = false;

    const base = src.split("?")[0];
    const dot = base.lastIndexOf(".");
    const baseNoExt = dot > -1 ? base.slice(0, dot) : base;

    const derived: SourceCandidate[] = deriveCandidates
      ? [
          { src: `${baseNoExt}.avif`, type: "image/avif" },
          { src: `${baseNoExt}.webp`, type: "image/webp" },
        ]
      : [];

    const candidates = (sources && sources.length ? sources : derived)
      // de-duplicate if any equals fallback
      .filter((s) => s.src !== src);

    if (candidates.length === 0) {
      setAvailable([]);
      return;
    }

    Promise.all(
      candidates.map(async (c) => {
        try {
          const res = await fetch(c.src, { method: "HEAD" });
          if (res.ok) return c;
        } catch {}
        return null;
      }),
    ).then((results) => {
      if (cancelled) return;
      const ok = results.filter(Boolean) as SourceCandidate[];
      // Keep priority: AVIF first, then WebP, then others
      ok.sort((a, b) => {
        const order = (t?: string) =>
          t === "image/avif" ? 0 : t === "image/webp" ? 1 : 2;
        return order(a.type) - order(b.type);
      });
      setAvailable(ok);
    });

    return () => {
      cancelled = true;
    };
  }, [src, deriveCandidates, JSON.stringify(sources || [])]);

  return (
    <picture>
      {available.map((c) => (
        <source key={c.src} srcSet={c.src} type={c.type || guessType(c.src)} />
      ))}
      {/* Fallback must exist */}
      <img src={src} alt={alt} {...imgProps} />
    </picture>
  );
}

export default OptimizedImage;
